import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { RotateCw, Check, Sun } from "lucide-react";

async function getCroppedDataUrl(src, cropPixels, rotation, brightness = 100) {
  const image = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
  const rad = (rotation * Math.PI) / 180;
  // Bounding box of the rotated image
  const bBoxW = Math.abs(Math.cos(rad) * image.width) + Math.abs(Math.sin(rad) * image.height);
  const bBoxH = Math.abs(Math.sin(rad) * image.width) + Math.abs(Math.cos(rad) * image.height);

  const tmp = document.createElement("canvas");
  tmp.width = bBoxW;
  tmp.height = bBoxH;
  const tctx = tmp.getContext("2d");
  tctx.translate(bBoxW / 2, bBoxH / 2);
  tctx.rotate(rad);
  if (brightness !== 100) tctx.filter = `brightness(${brightness}%)`;
  tctx.drawImage(image, -image.width / 2, -image.height / 2);

  const out = document.createElement("canvas");
  out.width = cropPixels.width;
  out.height = cropPixels.height;
  const octx = out.getContext("2d");
  octx.drawImage(
    tmp,
    cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height,
    0, 0, cropPixels.width, cropPixels.height,
  );
  // Auto-compress: ID photos ke liye 800px + q0.8 kaafi clear hai, storage halka rehta hai
  const maxDim = 800;
  if (out.width > maxDim || out.height > maxDim) {
    const s = Math.min(maxDim / out.width, maxDim / out.height);
    const fin = document.createElement("canvas");
    fin.width = Math.round(out.width * s);
    fin.height = Math.round(out.height * s);
    fin.getContext("2d").drawImage(out, 0, 0, fin.width, fin.height);
    return fin.toDataURL("image/jpeg", 0.8);
  }
  return out.toDataURL("image/jpeg", 0.8);
}

export default function PhotoCropDialog({ open, src, onClose, onDone }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [aspect, setAspect] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_, areaPixels) => setPixels(areaPixels), []);

  const apply = async () => {
    if (!pixels) return;
    setBusy(true);
    try {
      const dataUrl = await getCroppedDataUrl(src, pixels, rotation, brightness);
      onDone(dataUrl);
    } finally {
      setBusy(false);
      setZoom(1); setRotation(0); setBrightness(100); setCrop({ x: 0, y: 0 });
    }
  };

  const ratios = [
    { label: "1:1", value: 1 },
    { label: "3:4", value: 3 / 4 },
    { label: "4:3", value: 4 / 3 },
    { label: "Free", value: null },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="photo-crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Photo Adjust / Resize</DialogTitle>
        </DialogHeader>

        <div className="relative w-full h-72 bg-slate-900 rounded-lg overflow-hidden">
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect || undefined}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              restrictPosition={false}
              style={brightness !== 100 ? { mediaStyle: { filter: `brightness(${brightness}%)` } } : undefined}
            />
          )}
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {ratios.map((r) => (
              <Button key={r.label} size="sm" variant={aspect === r.value ? "default" : "outline"} onClick={() => setAspect(r.value)} className="h-8 text-xs" data-testid={`crop-ratio-${r.label}`}>
                {r.label}
              </Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => setRotation((r) => (r + 90) % 360)} className="h-8 gap-1 text-xs ml-auto" data-testid="crop-rotate-btn">
              <RotateCw className="h-3.5 w-3.5" /> Rotate
            </Button>
          </div>
          <div>
            <Label className="text-xs">Zoom</Label>
            <Slider min={1} max={4} step={0.05} value={[zoom]} onValueChange={(v) => setZoom(v[0])} data-testid="crop-zoom-slider" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs flex items-center gap-1"><Sun className="h-3.5 w-3.5" /> Brightness</Label>
              <span className="text-[11px] text-muted-foreground font-semibold" data-testid="crop-brightness-val">{brightness}%</span>
            </div>
            <Slider min={50} max={200} step={5} value={[brightness]} onValueChange={(v) => setBrightness(v[0])} data-testid="crop-brightness-slider" />
            <div className="flex justify-between mt-1">
              <button onClick={() => setBrightness((b) => Math.max(50, b - 10))} className="text-[11px] font-semibold text-slate-500 hover:text-slate-700" data-testid="crop-dim-btn">− Dark</button>
              {brightness !== 100 && <button onClick={() => setBrightness(100)} className="text-[11px] font-semibold text-cyan-600" data-testid="crop-brightness-reset">Reset</button>}
              <button onClick={() => setBrightness((b) => Math.min(200, b + 10))} className="text-[11px] font-semibold text-amber-600 hover:text-amber-700" data-testid="crop-brighten-btn">Bright +</button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={apply} disabled={busy} data-testid="crop-apply-btn" className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1.5">
            <Check className="h-4 w-4" /> {busy ? "..." : "Apply & Upload"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
